import React from 'react';
import { KindeProvider } from '@kinde-oss/kinde-auth-react';
import Router from './Router';
import { SettingsProvider, useSettings } from './_providers/SettingsProvider';
import { CookieConsentProvider, useCookieConsent } from './_providers/CookieConsentProvider';
import { CookieConsentBanner } from './_components/CookieConsentBanner/CookieConsentBanner';
import { initAnalytics } from './analytics/analytics';
import { shouldInitAnalytics } from './analytics/shouldInitAnalytics';

const KindeWrapper: React.FC = () => {
  const config = useSettings();
  const consent = useCookieConsent();

  // Analytics follows the exact same runtime-config pattern as the rest of
  // this settings object: only once /config.json has resolved AND the
  // visitor has accepted the cookie-consent banner does the app initialise
  // analytics with whatever config resolved. With no POSTHOG_KEY configured,
  // or with consent declined/unanswered, initAnalytics/createAnalyticsClient
  // stay inert — no vendor SDK construction, no network request, no cookie.
  // Called directly during render (idempotent — a module singleton, see
  // analytics.ts), so it settles in the same pass as everything else here
  // rather than a render-cycle later, and re-running this on every render
  // (e.g. after Accept is clicked) is safe.
  if (shouldInitAnalytics(config.isLoading, consent.status)) {
    initAnalytics({ posthogKey: config.posthogKey, posthogHost: config.posthogHost });
  }

  // Get the current URL dynamically
  const currentUrl = window.location.origin;

  if (config.isLoading) {
    return (
      <>
        <div className="flex items-center justify-center min-h-screen bg-gray-50">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-rose-500 mx-auto mb-4"></div>
            <p className="text-gray-600">Loading configuration...</p>
          </div>
        </div>
        <CookieConsentBanner />
      </>
    );
  }

  if (config.error) {
    return (
      <>
        <div className="flex items-center justify-center min-h-screen bg-gray-50">
          <div className="text-center">
            <div className="text-red-600 mb-4">
              <svg className="h-12 w-12 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <p className="text-gray-600">Failed to load configuration. Please refresh the page.</p>
          </div>
        </div>
        <CookieConsentBanner />
      </>
    );
  }

  return (
    <KindeProvider
      clientId={config.kindeClientId || "c05852f5e7e144318531558dc7f1c54e"}
      domain={config.kindeDomain || "https://invitewithlove.kinde.com"}
      redirectUri={`${currentUrl}/app`}
      logoutUri={currentUrl}
      audience={config.kindeAudience || "https://invitewithlove.com/api"}
    >
      {/* Rendered above the router so it appears on every route this app
          serves — admin routes under /app/* AND the public guest-facing
          routes (RSVP links, event homepages) that Router.tsx mounts with no
          auth requirement. A guest who never creates an account is still a
          visitor who can be tracked, so the gate has to cover them too. */}
      <Router />
      <CookieConsentBanner />
    </KindeProvider>
  );
};

const AppWithConfig: React.FC = () => {
  return (
    <SettingsProvider>
      <CookieConsentProvider>
        <KindeWrapper />
      </CookieConsentProvider>
    </SettingsProvider>
  );
};

export default AppWithConfig;